// src/components/PriceTag.tsx
// Reusable "special offer" price display: struck-through original price +
// prominent red discounted price + an auto-calculated discount badge.
import React from 'react';
import styled from 'styled-components';

type Size = 'sm' | 'md' | 'lg';
type Align = 'start' | 'center' | 'end';

interface PriceTagProps {
    /** Original (pre-discount) price, e.g. "199€". Optional — if omitted or not higher than `price`, no strikethrough/offer is shown. */
    originalPrice?: string;
    /** Current price the customer pays, e.g. "129€". */
    price: string;
    /** Text shown before the price, e.g. "From ". */
    prefix?: string;
    size?: Size;
    align?: Align;
    /** Text shown in the red offer pill. Defaults to "Special offer". */
    offerLabel?: string;
}

const SIZES: Record<Size, { current: string; original: string; label: string }> = {
    sm: { current: '1.4rem', original: '0.95rem', label: '0.6rem' },
    md: { current: '1.7rem', original: '1.05rem', label: '0.68rem' },
    lg: { current: '2.5rem', original: '1.35rem', label: '0.8rem' },
};

const Wrap = styled.div<{ $align: Align }>`
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: ${p => (p.$align === 'center' ? 'center' : p.$align === 'end' ? 'flex-end' : 'flex-start')};
`;

const OfferLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: #ffffff;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    line-height: 1;
    padding: 0.28rem 0.6rem;
    border-radius: 999px;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
    white-space: nowrap;
`;

const Row = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    flex-wrap: wrap;
`;

const Prefix = styled.span`
    color: #6b7280;
    font-weight: 600;
`;

const Original = styled.span`
    color: #9ca3af;
    font-weight: 600;
    text-decoration: line-through;
`;

const Current = styled.span`
    color: #ef4444;
    font-weight: 800;
    line-height: 1;
`;

const parseAmount = (s?: string): number => {
    if (!s) return 0;
    const match = s.match(/[\d]+(?:[.,]\d+)?/);
    return match ? parseFloat(match[0].replace(',', '.')) : 0;
};

const PriceTag: React.FC<PriceTagProps> = ({
    originalPrice,
    price,
    prefix,
    size = 'md',
    align = 'start',
    offerLabel = 'Special offer',
}) => {
    const s = SIZES[size];
    const orig = parseAmount(originalPrice);
    const now = parseAmount(price);
    const hasDiscount = orig > 0 && now > 0 && now < orig;
    const pct = hasDiscount ? Math.round((1 - now / orig) * 100) : 0;

    return (
        <Wrap $align={align}>
            {hasDiscount && (
                <OfferLabel style={{ fontSize: s.label }}>
                    🔥 {offerLabel}{pct > 0 ? ` · −${pct}%` : ''}
                </OfferLabel>
            )}
            <Row>
                {prefix && <Prefix style={{ fontSize: s.original }}>{prefix}</Prefix>}
                {hasDiscount && <Original style={{ fontSize: s.original }}>{originalPrice}</Original>}
                <Current style={{ fontSize: s.current }}>{price}</Current>
            </Row>
        </Wrap>
    );
};

export default PriceTag;
